import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Spinner } from 'reactstrap';

import ControldocAiChatMessage from './chat_message';
import ControldocAiChatInput from './chat_input';

import consumer from '../../../../channels/consumer';

import {
  indexControldocAiChatConversations,
  createControldocAiChatConversation,
  indexControldocAiChatMessages,
  createControldocAiChatMessage
} from './axios';

const ControldocAiChatInterface = (props) => {
  const {
    conversationId,
    configuration: {
      enabledFileButton
    },
  } = props;

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Streaming
  const streamSubscriptionRef = useRef(null);
  const [streaming, setStreaming] = useState({ active: false, raw: '' });

  const [showGoToBottom, setShowGoToBottom] = useState(false);
  const messagesContainerRef = useRef(null);

  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    has_next_page: false
  });

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 1000;
    setShowGoToBottom(!nearBottom);
  };

  useEffect(() => {
    if (!activeConversation?.id) return;

    const pendingStreamTextRef = { current: '' };
    const isApplyScheduledRef = { current: false };

    const applyPendingStreamText = () => {
      isApplyScheduledRef.current = false;
      const pendingText = pendingStreamTextRef.current;
      if (!pendingText) return;

      pendingStreamTextRef.current = '';
      setStreaming(stream => ({ ...stream, raw: stream.raw + pendingText }));
    };

    const scheduleApplyPendingText = () => {
      if (isApplyScheduledRef.current) return;
      isApplyScheduledRef.current = true;
      setTimeout(applyPendingStreamText, 80);
    };

    streamSubscriptionRef.current = consumer.subscriptions.create(
      { channel: 'Controldoc::Ai::ChatConversationChannel', conversation_id: activeConversation.id },
      {
        received: (data) => {
          if (data.type === 'tool') {
            const { id, content, status } = data;

            setMessages(prev => {
              const index = id ? _.findIndex(prev, message => message.meta === 'tool' && message.tool_id === id) : -1;
              const item = { role: 'system', meta: 'tool', tool_id: id, tool_name: content, content, status };

              if (index === -1) {
                return [...prev, item];
              }

              prev[index] = item;
              return prev;
            });
            return;
          }

          if (data.type === 'token') {
            setStreaming(stream => (stream.active ? stream : { active: true, raw: '' }));
            pendingStreamTextRef.current += data.content || '';
            scheduleApplyPendingText();
            scrollToBottom();
            return;
          }

          if (data.type === 'stream_end') {
            applyPendingStreamText(); // asegura el último tramo
            setMessages(prev => [...prev, { role: 'system', content: data.content || streaming.raw }]);
            setStreaming({ active: false, raw: '' });
            pendingStreamTextRef.current = '';
            isApplyScheduledRef.current = false;
            return;
          }
        }
      }
    );

    return () => {
      if (streamSubscriptionRef.current) {
        consumer.subscriptions.remove(streamSubscriptionRef.current);
        streamSubscriptionRef.current = null;
      }
    };
  }, [activeConversation?.id]);

  useEffect(() => {
    const chatEl = document.getElementById('offcanvasChat');

    // En caso de que el chat no este disponible y se carga el componente en otra vista
    if (!chatEl) {
      fetchConversations();
      return;
    }

    if (chatEl.classList.contains('show')) {
      fetchConversations();
    }

    chatEl.addEventListener('show.bs.offcanvas', function handleShow() {
      fetchConversations();
      chatEl.removeEventListener('show.bs.offcanvas', handleShow);
    });

    return () => {
      chatEl.removeEventListener('show.bs.offcanvas', handleShow);
    };

  }, []);

  useEffect(() => {
    if (messages.length > 0 && !showGoToBottom) {
      scrollToBottom();
    }
  }, [messages, showGoToBottom]);

  const fetchConversations = () => {
    indexControldocAiChatConversations(response => {
      if (response?.status === 200) {
        let conversation = response?.data?.conversations[0]

        if (conversationId) {
          conversation = _.find(response?.data?.conversations, { id: conversationId });
        }

        setConversations(conversation);

        if (response?.data?.conversations?.length > 0 && !activeConversation) {
          setActiveConversation(conversation);
          fetchMessages(conversation?.id);
        }
      }
    });
  };

  const fetchMessages = (conversationId, isLoadMore = false) => {
    const page = isLoadMore ? pagination.current_page + 1 : 1;

    indexControldocAiChatMessages(conversationId, page, response => {
      if (response?.status === 200) {
        if (isLoadMore) {
          setMessages(prev => [...response.data?.messages, ...prev]);
        } else {
          setMessages(response.data?.messages);
        }
        setPagination(response.data.pagination);
      }
    });
  };

  const onLoadMore = () => {
    if (pagination.has_next_page) {
      fetchMessages(activeConversation?.id, true);
    }
  };

  const onSendMessage = (formData, afterCallback) => {
    setLoading(true)
    setMessages(prev => [...prev, { role: 'user', content: formData.get('message') }]);
    scrollToBottom();

    if (!activeConversation) {
      createControldocAiChatConversation(response => {
        if (response?.status === 200) {
          setConversations([response?.data]);
          setActiveConversation(response?.data);

          createControldocAiChatMessage(formData, response?.data, response => {
            // if (response?.status === 200) {
            //   setMessages(prev => [...prev, { role: 'system', content: response?.data?.message, isTyping: true }]);
            //   if (response?.data?.url) window.location.assign(response?.data?.url);
            // } else if (response?.status === 503) {
            //   setMessages(prev => [...prev, { role: 'error', content: response?.data?.message }]);
            // }
            setLoading(false);
            if (afterCallback) afterCallback();
          });
        }
      });
    } else {
      createControldocAiChatMessage(formData, activeConversation, response => {
        // if (response?.status === 200) {
        //   setMessages(prev => {
        //     return [...prev, { role: 'system', content: response?.data?.message, isTyping: true }]
        //   });
        //   if (response?.data?.url) window.location.assign(response?.data?.url);
        // } else if (response?.status === 503) {
        //   setMessages(prev => [...prev, { role: 'error', content: response?.data?.message }]);
        // }
        setLoading(false);
        if (afterCallback) afterCallback();
      });
    }

    scrollToBottom();
  };

  const groupMessagesForRender = (messagelist) => {
    const groupedBlocks = [];

    _.forEach(messagelist, message => {
      if (message.meta === 'tool') {
        const last = _.last(groupedBlocks);

        if (last && last.type === 'tool_group') {
          last.items.push(message);
        } else {
          groupedBlocks.push({ type: 'tool_group', items: [message] });
        }
      } else {
        groupedBlocks.push({ type: 'message', message: message });
      }
    });
    return groupedBlocks;
  };

  const listenMessages = useMemo(() => {
    const messagelist = streaming.active
      ? [...messages, { role: 'system', raw: streaming.raw, streaming: true }]
      : messages;

    const groupedBlocks = groupMessagesForRender(messagelist);

    return _.map(groupedBlocks, (block, index) => {
      if (block.type === 'tool_group') {
        return (
          <div key={`tg-${index}`} className="tool-group">
            {
              _.map(block.items, (message, messageIndex) => {
                return (
                  <ControldocAiChatMessage
                    key={`tg-${index}-${messageIndex}`}
                    message={message}
                    isLastAssistant={false}
                  />
                )
              })
            }
          </div>
        );
      }

      const isLast = index === groupedBlocks.length - 1;
      const isLastAssistant = isLast && block.message.role === 'system';

      // si no tiene content y no es tool ni streaming, omite
      if (!block.message.content && !block.message.streaming) return null;

      return (
        <ControldocAiChatMessage
          key={`m-${index}`}
          message={block.message}
          isLastAssistant={isLastAssistant}
        />
      );
    });
  }, [messages, streaming.active, streaming.raw]);

  const listMessagesContainer = () => {
    if (messages.length > 0) {
      return (
        <div className="messages-container pb-0" ref={messagesContainerRef} onScroll={handleScroll}>
          {buttonLoadMoreMessages()}
          {listenMessages}
          {spinner()}
          {buttonGoToBottom()}
          <div ref={messagesEndRef}></div>
        </div>
      )
    } else {
      return (
        <div className="text-center">
          <h1 className="fw-bold fs-3 mb-4">¿Qué quieres hacer hoy?</h1>
        </div>
      )
    }
  }

  const buttonLoadMoreMessages = () => {
    if (pagination.has_next_page) {
      return (
        <div className="text-center mb-3">
          <button
            className="btn btn-outline-primary btn-sm w-100"
            onClick={onLoadMore}
            disabled={loading}
            style={{ cursor: 'pointer' }}
          >
            Cargar mensajes anteriores
          </button>
        </div>
      )
    }
  }

  const buttonGoToBottom = () => {
    if (!showGoToBottom) return null;
    return (
      <div className="text-center mb-3">
        <button
          onClick={scrollToBottom}
          className="btn btn-sm btn-info go-to-bottom-button"
        >
          <i className="fas fa-angle-down mr-2"></i>
          Ir a Último Mensaje
        </button>
      </div>
    );
  }

  const spinner = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center">
          <Spinner color="primary" style={{ width: '2rem', height: '2rem' }} />
        </div>
      )
    }
  }

  return (
    <div className="chat-interface">
      <div>
        <div className={`chat-main ${messages.length > 0 ? '' : 'centered-content'}`}>
          <div className="chat-main-container">
            {listMessagesContainer()}
          </div>
          <div className="sticky-bottom">
            <div className="container messages-input-container">
              <ControldocAiChatInput
                onSend={onSendMessage}
                disabled={loading}
                messagesCount={messages.length}
                configuration={{
                  enabledFileButton: enabledFileButton
                }}
              />
              {/* <div className="suggested-options container py-3 d-flex flex-nowrap">
                <button type="button" className="btn btn-light btn-sm bg-real-light mr-2">
                  <i className="fas fa-search mr-2 text-success"></i>
                  Buscar
                </button>
                <button type="button" className="btn btn-light btn-sm bg-real-light mr-2">
                  <i className="fas fa-file-alt mr-2 text-info"></i>
                  Solicitud de Documentos
                </button>
                <button type="button" className="btn btn-light btn-sm bg-real-light mr-2">
                  <i className="fas fa-upload mr-2 text-warning"></i>
                  Carga Masiva
                </button>
                <button type="button" className="btn btn-light btn-sm bg-real-light mr-2">
                  <i className="fas fa-bullhorn mr-2 text-secondary"></i>
                  Notificaciones
                </button>
                <button type="button" className="btn btn-light btn-sm bg-real-light mr-2">
                  <i className="fas fa-question-circle mr-2 text-primary"></i>
                  Centro de Ayuda
                </button>
                <button type="button" className="btn btn-light btn-sm bg-real-light mr-2">
                  <i className="fas fa-plus"></i>
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ControldocAiChatInterface;
